import { serializeJsonLd } from "@/lib/safeJsonLd";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { brand } from "@/config/brand";
import { useLanguage } from "@/hooks/useLanguage";
import { getSeoForPage } from "@/config/seoTranslations";

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
  keywords?: string;
}

const langToHtmlLang: Record<string, string> = {
  iw: "he",
  ru: "ru",
  en: "en",
};

const langToLocale: Record<string, string> = {
  iw: "he_IL",
  ru: "ru_RU",
  en: "en_US",
};

const SEOHead = ({
  title,
  description,
  image,
  url,
  type = "website",
  noIndex = false,
  jsonLd,
  keywords,
}: SEOHeadProps) => {
  const { lang } = useLanguage();
  const location = useLocation();
  const pathname = location.pathname;

  // Get translated SEO strings for current language
  const translated = getSeoForPage(lang, pathname);

  // Use provided title/description (e.g. product-specific) or fall back to translated version
  const resolvedTitle = title || translated.title;
  const resolvedDesc = description || translated.description;

  const fullTitle = resolvedTitle
    ? `${resolvedTitle} | ${translated.brandName}`
    : `${translated.brandName} | ${translated.title}`;
  const desc = resolvedDesc;
  const ogImage = image || brand.ogImage;
  const baseDomain = `https://${brand.domain}`;
  const pageUrl = `${baseDomain}${pathname}`;
  const canonical = url || pageUrl;
  const htmlLang = langToHtmlLang[lang] || "he";
  const ogLocale = langToLocale[lang] || brand.locale;

  return (
    <Helmet>
      <html lang={htmlLang} dir={lang === "iw" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Language / hreflang — Hebrew (original), Russian, English (US) */}
      <link rel="alternate" hrefLang="he" href={pageUrl} />
      <link rel="alternate" hrefLang="ru" href={pageUrl} />
      <link rel="alternate" hrefLang="en" href={pageUrl} />
      <link rel="alternate" hrefLang="x-default" href={pageUrl} />
      <meta httpEquiv="content-language" content={htmlLang} />

      {/* Prerender hint for crawlers */}
      <meta name="fragment" content="!" />

      {/* AI Crawler directives */}
      <meta name="ai-content-declaration" content="human-created" />
      <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={translated.brandName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {serializeJsonLd({ "@context": "https://schema.org", ...jsonLd })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
